import React from "react";
import { X, Download, Lock, Crown } from "lucide-react";
import { Button } from "../../ui/button";
import { Y } from "./constants";

const PLAN_LBL = { trial: "Gratis", pro: "Pro", premium: "Premium", vip: "VIP" };

// Export dialog. Options above the user's plan are locked behind an upgrade.
const QLBL = { trial: "Básica", pro: "Alta", premium: "Máxima", vip: "Máxima" };

const Quota = ({ usage }) => {
  if (!usage) return null;
  const out = usage.exports_remaining <= 0;
  return (
    <div className={`mb-4 rounded-xl border p-3 text-xs ${out ? "border-red-500/40 bg-red-500/10" : "border-white/10 bg-white/5"}`} data-testid="export-quota">
      <div className="flex items-center justify-between"><span className="text-zinc-300">Exportaciones {usage.period === "lifetime" ? "gratis" : "este mes"}</span><b className={out ? "text-red-300" : "text-white"} data-testid="export-quota-count">{usage.exports_used} / {usage.exports_limit}</b></div>
      <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, usage.exports_used / usage.exports_limit * 100)}%`, background: out ? "#ef4444" : Y }} /></div>
      {out && (<div className="mt-3 flex items-center justify-between gap-2" data-testid="export-quota-blocked">
        <span className="text-red-200">Has usado todas tus exportaciones.</span>
        <a href="/#pricing" data-testid="export-upgrade" className="shrink-0 rounded-lg px-3 py-1.5 font-extrabold text-black" style={{ background: Y }}>Mejorar plan</a>
      </div>)}
    </div>
  );
};

export const ExportModal = ({ exp, setExp, limits, usage, exporting, progress = 0, onExport, onClose, total }) => {
  const blocked = usage && usage.exports_remaining <= 0;
  const maxRes = limits?.max_resolution || 720, maxFps = limits?.max_fps || 30;
  const groups = [
    ["Resolución", "resolution", [[720, "720p"], [1080, "1080p"], [1440, "2K"], [2160, "4K"]], (v) => v > maxRes],
    ["FPS", "fps", [[24, "24"], [30, "30"], [40, "40"], [60, "60"]], (v) => v > maxFps],
    ["Formato", "fmt", [["mp4", "MP4"], ["mov", "MOV"]], () => false],
  ];
  const upgrade = () => { window.location.href = "/#pricing"; };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="export-modal">
        <div className="flex items-center justify-between mb-1"><p className="font-bold">Exportar vídeo</p><button onClick={onClose} data-testid="export-close"><X className="h-5 w-5 text-zinc-400" /></button></div>
        <p className="text-xs text-zinc-500 mb-4">Duración {total.toFixed(1)}s · Plan <span className="text-white font-semibold" data-testid="export-plan">{PLAN_LBL[limits?.plan] || "Gratis"}</span> · Calidad <span className="text-white font-semibold" data-testid="export-quality">{QLBL[limits?.plan] || "Básica"}</span></p>
        <Quota usage={usage} />
        {groups.map(([lbl, key, opts, locked]) => (
          <div key={key} className="mb-4"><p className="text-xs text-zinc-400 mb-1.5">{lbl}</p>
            <div className="flex gap-2">{opts.map(([v, l]) => {
              const lk = locked(v);
              return (
                <button key={v} data-testid={`exp-${key}-${v}`} onClick={() => (lk ? null : setExp((p) => ({ ...p, [key]: v })))} disabled={lk} title={lk ? "Disponible en planes de pago" : ""}
                  className={`flex-1 rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1 ${lk ? "bg-white/5 text-zinc-600 cursor-not-allowed" : exp[key] === v ? "text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
                  style={!lk && exp[key] === v ? { background: Y } : {}}>{lk && <Lock className="h-3 w-3" />}{l}</button>
              );
            })}</div>
          </div>
        ))}
        {(maxRes < 2160 || maxFps < 60) && (
          <div className="rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-3 mb-3 text-xs text-zinc-300" data-testid="export-upgrade-box">
            <p className="flex items-center gap-1.5 font-semibold text-white"><Crown className="h-3.5 w-3.5" style={{ color: Y }} />Alta calidad solo en planes de pago</p>
            <p className="mt-1 text-zinc-400">{limits?.plan === "trial" || !limits ? "Gratis: 720p · 30 fps · calidad básica con marca de agua. Pro: 1080p · 60 fps · alta calidad. Premium: 2K. VIP: 4K · máxima nitidez." : "Mejora a VIP para exportar en 4K."}</p>
            <button onClick={upgrade} data-testid="export-upgrade-btn" className="mt-2 font-bold" style={{ color: Y }}>Ver planes →</button>
          </div>
        )}
        {exporting && (
          <div data-testid="export-progress">
            <div className="flex justify-between text-xs text-zinc-400 mb-1.5"><span>Renderizando vídeo…</span><span className="font-mono" data-testid="export-progress-pct">{progress}%</span></div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.max(3, progress)}%`, background: Y }} /></div>
          </div>
        )}
        <Button onClick={onExport} disabled={exporting || blocked} data-testid="export-confirm" className="w-full font-extrabold text-black rounded-xl mt-1 hover:opacity-90" style={{ background: Y }}>
          {exporting ? "Exportando…" : <><Download className="h-4 w-4 mr-1.5" />Exportar</>}
        </Button>
      </div>
    </div>
  );
};
